import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { UserCommunication, WhatsAppIntegrationView } from '@this+that/models';
import log from 'loglevel';

import { createApiWithAuth } from '../utils/axios';
import { requireQueryData } from '../utils/requireQueryData';

const GET_TEAM_INSTAGRAM_INTEGRATIONS = gql`
  query getTeamInstagramIntegrations($teamId: String!) {
    getTeamInstagramIntegrations(teamId: $teamId) {
      platformAccountId
      igUsername
      teamId
      createdAt
      updatedAt
    }
  }
`;

export interface InstagramIntegration {
  platformAccountId: string;
  igUsername: string;
  teamId: string;
  createdAt: number;
  updatedAt: number;
}

interface GetTeamInstagramIntegrationsData {
  getTeamInstagramIntegrations: InstagramIntegration[];
}

export function useGetTeamInstagramIntegrations() {
  const { loading, refetch } = useQuery<GetTeamInstagramIntegrationsData>(
    GET_TEAM_INSTAGRAM_INTEGRATIONS,
    {
      skip: true,
    },
  );

  const getTeamInstagramIntegrations = useCallback(
    async (teamId: string) => {
      try {
        const data = requireQueryData(await refetch({ teamId }));

        return data.getTeamInstagramIntegrations ?? [];
      } catch (error) {
        log.error('Error fetching Instagram integrations:', error);
        throw error;
      }
    },
    [refetch],
  );

  return { getTeamInstagramIntegrations, loading };
}

const GET_TEAM_INSTAGRAM_MEDIA = gql`
  query getTeamInstagramMedia(
    $teamId: String!
    $igAccountId: String!
    $limit: Int
    $cursor: String
  ) {
    getTeamInstagramMedia(
      teamId: $teamId
      igAccountId: $igAccountId
      limit: $limit
      cursor: $cursor
    ) {
      media {
        mediaId
        caption
        mediaType
        mediaProductType
        permalink
        thumbnailUrl
        timestamp
        commentsCount
      }
      nextCursor
    }
  }
`;

export interface InstagramMediaSummary {
  mediaId: string;
  caption?: string;
  mediaType?: string;
  mediaProductType?: string;
  permalink?: string;
  thumbnailUrl?: string;
  timestamp?: string;
  commentsCount?: number;
}

export interface InstagramMediaPage {
  media: InstagramMediaSummary[];
  nextCursor?: string | null;
}

interface GetTeamInstagramMediaData {
  getTeamInstagramMedia: InstagramMediaPage;
}

export interface GetTeamInstagramMediaParams {
  teamId: string;
  igAccountId: string;
  limit?: number;
  cursor?: string;
}

const EMPTY_INSTAGRAM_MEDIA_PAGE: InstagramMediaPage = {
  media: [],
  nextCursor: null,
};

export function useGetTeamInstagramMedia() {
  const { loading, refetch } = useQuery<GetTeamInstagramMediaData>(
    GET_TEAM_INSTAGRAM_MEDIA,
    { skip: true },
  );

  const getTeamInstagramMedia = useCallback(
    async (params: GetTeamInstagramMediaParams) => {
      // errorPolicy is 'all', so a non-null `data` does not mean the query succeeded
      const { data, error } = await refetch(params);

      if (error) {
        throw error;
      }

      return data?.getTeamInstagramMedia ?? EMPTY_INSTAGRAM_MEDIA_PAGE;
    },
    [refetch],
  );

  return { getTeamInstagramMedia, loading };
}

export const META_COMMUNICATION_FIELDS = gql`
  fragment MetaCommunicationFields on UserCommunication {
    userId
    messageId
    createdAt
    author
    authorUserId
    authorAvatarUrl
    userEmailAddress
    messagePreview
    messageText
    messageRawText
    channel
    channelName
    platform
    messageUrl
    labelIds
    s3Key
    attachments {
      fileName
      fileType
      size
      previewUrl
      contentUrl
      alt
    }
  }
`;

const GET_INSTAGRAM_CONVERSATIONS = gql`
  query getInstagramConversations($teamId: String!, $igAccountId: String!) {
    getInstagramConversations(teamId: $teamId, igAccountId: $igAccountId) {
      igAccountId
      conversationId
      teamId
      participantIgsid
      participantName
      participantUsername
      participantAvatarUrl
      createdAt
      refreshedAt
      lastCommunication {
        ...MetaCommunicationFields
      }
    }
  }
  ${META_COMMUNICATION_FIELDS}
`;

export interface InstagramConversation {
  igAccountId: string;
  conversationId: string;
  teamId: string;
  participantIgsid: string;
  participantName?: string;
  participantUsername?: string;
  participantAvatarUrl?: string;
  createdAt: number;
  refreshedAt: number;
  lastCommunication?: UserCommunication;
}

interface GetInstagramConversationsData {
  getInstagramConversations: InstagramConversation[];
}

export function useGetInstagramConversations() {
  const { loading, refetch } = useQuery<GetInstagramConversationsData>(
    GET_INSTAGRAM_CONVERSATIONS,
    { skip: true },
  );

  const getInstagramConversations = useCallback(
    async (teamId: string, igAccountId: string) => {
      try {
        const { data } = await refetch({ teamId, igAccountId });
        return data?.getInstagramConversations ?? [];
      } catch (error) {
        log.error('Error fetching Instagram conversations:', error);
        return [] as InstagramConversation[];
      }
    },
    [refetch],
  );

  return { getInstagramConversations, loading };
}

const GET_INSTAGRAM_CONVERSATION_MESSAGES = gql`
  query getInstagramConversationMessages(
    $teamId: String!
    $igAccountId: String!
    $conversationId: String!
    $limit: Int!
    $exclusiveStartKey: String
  ) {
    getInstagramConversationMessages(
      teamId: $teamId
      igAccountId: $igAccountId
      conversationId: $conversationId
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
    ) {
      userCommunications {
        ...MetaCommunicationFields
      }
      exclusiveStartKey
    }
  }
  ${META_COMMUNICATION_FIELDS}
`;

export interface PaginatedUserCommunications {
  userCommunications: UserCommunication[];
  exclusiveStartKey: string | null;
}

interface GetInstagramConversationMessagesData {
  getInstagramConversationMessages: PaginatedUserCommunications | null;
}

export interface GetInstagramConversationMessagesParams {
  teamId: string;
  igAccountId: string;
  conversationId: string;
  limit: number;
  exclusiveStartKey?: string;
}

const EMPTY_MESSAGES_PAGE: PaginatedUserCommunications = {
  userCommunications: [],
  exclusiveStartKey: null,
};

export function useGetInstagramConversationMessages() {
  const { loading, refetch } = useQuery<GetInstagramConversationMessagesData>(
    GET_INSTAGRAM_CONVERSATION_MESSAGES,
    { skip: true },
  );

  const getInstagramConversationMessages = useCallback(
    async (params: GetInstagramConversationMessagesParams) => {
      // errorPolicy is 'all', so a non-null `data` does not mean the query succeeded
      const { data, error } = await refetch(params);

      if (error) {
        throw error;
      }

      return data?.getInstagramConversationMessages ?? EMPTY_MESSAGES_PAGE;
    },
    [refetch],
  );

  return { getInstagramConversationMessages, loading };
}

// ─── REST ─────────────────────────────────────────────────────────────────────

let instagramApi: ReturnType<typeof createApiWithAuth> | null = null;

const getInstagramApi = () => {
  if (!instagramApi) {
    const baseUrl = import.meta.env.VITE_INSTAGRAM_BASE_URL;
    if (!baseUrl) {
      throw new Error('Instagram base URL is not set');
    }
    instagramApi = createApiWithAuth(baseUrl);
  }
  return instagramApi;
};

// === Instagram ===
const INSTAGRAM_OAUTH_URL_PATH = '/oauth-url';
const INSTAGRAM_INITIAL_SYNC_PATH = '/initial-sync';
const INSTAGRAM_UNSUBSCRIBE_PATH = '/unsubscribe';
const INSTAGRAM_SEND_MESSAGE_PATH = '/send-message';

interface InstagramInitialSyncResponse {
  message: string;
  igAccountId: string;
  igUsername: string;
}

interface InstagramSendMessageResponse {
  messageId: string;
  recipientId: string;
}

export const INSTAGRAM_OUTSIDE_ALLOWED_WINDOW_ERROR_CODE =
  'INSTAGRAM_OUTSIDE_ALLOWED_WINDOW';

export const INSTAGRAM_UNSUPPORTED_LOGIN_METHOD_ERROR_CODE =
  'INSTAGRAM_UNSUPPORTED_LOGIN_METHOD';

export const INSTAGRAM_INITIAL_SYNC_FAILED_ERROR_CODE =
  'INSTAGRAM_INITIAL_SYNC_FAILED';

export const getInstagramIntegrationUrl = async (
  teamId: string,
): Promise<{ message: string }> => {
  try {
    const { data } = await getInstagramApi().get(INSTAGRAM_OAUTH_URL_PATH, {
      params: { teamId },
    });
    return data;
  } catch (error) {
    log.error('Error fetching Instagram OAuth URL:', error);
    throw error;
  }
};

export const scheduleInstagramInitialSync = async (params: {
  teamId: string;
  code: string;
  state: string;
}): Promise<InstagramInitialSyncResponse> => {
  try {
    const { data } = await getInstagramApi().post(
      INSTAGRAM_INITIAL_SYNC_PATH,
      params,
    );
    return data;
  } catch (error) {
    log.error('Error scheduling Instagram initial sync:', error);
    throw error;
  }
};

/**
 * Sends a plain-text message to an Instagram recipient via the backend
 * POST /send-message endpoint, which proxies to the Instagram Graph API.
 *
 * Meta 24-hour messaging window constraint: Instagram only permits outbound
 * messages within 24 hours of the recipient's last inbound message. Outside
 * that window the backend returns HTTP 400 with body
 * `{ message: { code: 'INSTAGRAM_OUTSIDE_ALLOWED_WINDOW', message } }`.
 * Callers can detect this with `getRestErrorCode(error)` (`utils/restErrors.ts`),
 * which reads `error.response?.data?.message?.code`, then compare it against
 * `INSTAGRAM_OUTSIDE_ALLOWED_WINDOW_ERROR_CODE` and render a specific UI state
 * rather than a generic error toast.
 *
 * Only plain text is supported today; attachment support will be added later.
 */
export const sendInstagramMessage = async (params: {
  teamId: string;
  igAccountId: string;
  recipientId: string;
  text: string;
}): Promise<InstagramSendMessageResponse> => {
  try {
    const { data } = await getInstagramApi().post(
      INSTAGRAM_SEND_MESSAGE_PATH,
      params,
    );
    return data;
  } catch (error) {
    log.error('Error sending Instagram message:', error);
    throw error;
  }
};

export const disconnectInstagramIntegration = async (params: {
  teamId: string;
  igAccountId: string;
  deleteData: boolean;
}): Promise<void> => {
  try {
    await getInstagramApi().delete(
      `${INSTAGRAM_UNSUBSCRIBE_PATH}/${params.teamId}/${params.igAccountId}`,
      { params: { deleteData: params.deleteData } },
    );
  } catch (error) {
    log.error('Error disconnecting Instagram integration:', error);
    throw error;
  }
};

// ─── WhatsApp ─────────────────────────────────────────────────────────────────

const GET_USER_WHATSAPP_INTEGRATIONS = gql`
  query getUserWhatsAppIntegrations {
    getUserWhatsAppIntegrations {
      phoneNumberId
      displayPhoneNumber
      wabaId
      createdAt
      updatedAt
    }
  }
`;

interface GetUserWhatsAppIntegrationsData {
  getUserWhatsAppIntegrations: WhatsAppIntegrationView[];
}

export function useGetUserWhatsAppIntegrations() {
  const { loading, refetch } = useQuery<GetUserWhatsAppIntegrationsData>(
    GET_USER_WHATSAPP_INTEGRATIONS,
    { skip: true },
  );

  const getUserWhatsAppIntegrations = useCallback(async () => {
    try {
      const data = requireQueryData(await refetch());
      return data.getUserWhatsAppIntegrations ?? [];
    } catch (error) {
      log.error('Error fetching WhatsApp integrations:', error);
      throw error;
    }
  }, [refetch]);

  return { getUserWhatsAppIntegrations, loading };
}

// === WhatsApp REST ===

let whatsAppApi: ReturnType<typeof createApiWithAuth> | null = null;

const getWhatsAppApi = () => {
  if (!whatsAppApi) {
    const baseUrl = import.meta.env.VITE_WHATSAPP_BASE_URL;
    if (!baseUrl) {
      throw new Error('WhatsApp base URL is not set');
    }
    whatsAppApi = createApiWithAuth(baseUrl);
  }
  return whatsAppApi;
};

const WHATSAPP_INITIAL_SYNC_PATH = '/initial-sync';
const WHATSAPP_UNSUBSCRIBE_PATH = '/unsubscribe';

interface WhatsAppInitialSyncParams {
  code: string;
  phoneNumberId: string;
  wabaId: string;
}

interface WhatsAppInitialSyncResponse {
  message: string;
  phoneNumber: string;
}

export const scheduleWhatsAppInitialSync = async (
  payload: WhatsAppInitialSyncParams,
): Promise<WhatsAppInitialSyncResponse> => {
  try {
    const { data } = await getWhatsAppApi().post(
      WHATSAPP_INITIAL_SYNC_PATH,
      payload,
    );

    return data;
  } catch (error) {
    log.error('Error scheduling WhatsApp initial sync:', error);
    throw error;
  }
};

export const disconnectWhatsAppAccount = async (params: {
  phoneNumberId: string;
  deleteData: boolean;
}): Promise<void> => {
  try {
    await getWhatsAppApi().delete(
      `${WHATSAPP_UNSUBSCRIBE_PATH}/${params.phoneNumberId}`,
      { params: { deleteData: params.deleteData } },
    );
  } catch (error) {
    log.error('Error disconnecting WhatsApp account:', error);
    throw error;
  }
};

// ─── Facebook ─────────────────────────────────────────────────────────────────

const GET_TEAM_FACEBOOK_INTEGRATIONS = gql`
  query getTeamFacebookIntegrations($teamId: String!) {
    getTeamFacebookIntegrations(teamId: $teamId) {
      platformAccountId
      pageId
      pageName
      pageUsername
      teamId
      createdAt
      updatedAt
    }
  }
`;

export interface FacebookIntegration {
  platformAccountId: string;
  pageId: string;
  pageName: string;
  pageUsername?: string;
  teamId: string;
  createdAt: number;
  updatedAt: number;
}

interface GetTeamFacebookIntegrationsData {
  getTeamFacebookIntegrations: FacebookIntegration[];
}

export function useGetTeamFacebookIntegrations() {
  const { loading, refetch } = useQuery<GetTeamFacebookIntegrationsData>(
    GET_TEAM_FACEBOOK_INTEGRATIONS,
    {
      skip: true,
    },
  );

  const getTeamFacebookIntegrations = useCallback(
    async (teamId: string) => {
      try {
        const data = requireQueryData(await refetch({ teamId }));

        return data.getTeamFacebookIntegrations ?? [];
      } catch (error) {
        log.error('Error fetching Facebook integrations:', error);
        throw error;
      }
    },
    [refetch],
  );

  return { getTeamFacebookIntegrations, loading };
}

const GET_FACEBOOK_CONVERSATIONS = gql`
  query getFacebookConversations($teamId: String!, $pageId: String!) {
    getFacebookConversations(teamId: $teamId, pageId: $pageId) {
      pageId
      conversationId
      teamId
      participantPsid
      participantName
      participantAvatarUrl
      createdAt
      refreshedAt
      lastCommunication {
        ...MetaCommunicationFields
      }
    }
  }
  ${META_COMMUNICATION_FIELDS}
`;

export interface FacebookConversation {
  pageId: string;
  conversationId: string;
  teamId: string;
  participantPsid: string;
  participantName?: string;
  participantAvatarUrl?: string;
  createdAt: number;
  refreshedAt: number;
  lastCommunication?: UserCommunication;
}

interface GetFacebookConversationsData {
  getFacebookConversations: FacebookConversation[];
}

export function useGetFacebookConversations() {
  const { loading, refetch } = useQuery<GetFacebookConversationsData>(
    GET_FACEBOOK_CONVERSATIONS,
    { skip: true },
  );

  const getFacebookConversations = useCallback(
    async (teamId: string, pageId: string) => {
      try {
        const { data } = await refetch({ teamId, pageId });
        return data?.getFacebookConversations ?? [];
      } catch (error) {
        log.error('Error fetching Facebook conversations:', error);
        return [] as FacebookConversation[];
      }
    },
    [refetch],
  );

  return { getFacebookConversations, loading };
}

// ─── REST ─────────────────────────────────────────────────────────────────────

let facebookApi: ReturnType<typeof createApiWithAuth> | null = null;

const getFacebookApi = () => {
  if (!facebookApi) {
    const baseUrl = import.meta.env.VITE_FACEBOOK_BASE_URL;
    if (!baseUrl) {
      throw new Error('Facebook base URL is not set');
    }
    facebookApi = createApiWithAuth(baseUrl);
  }
  return facebookApi;
};

const FACEBOOK_OAUTH_URL_PATH = '/oauth-url';
const FACEBOOK_INITIAL_SYNC_PATH = '/initial-sync';
const FACEBOOK_UNSUBSCRIBE_PATH = '/unsubscribe';
const FACEBOOK_SEND_MESSAGE_PATH = '/send-message';

interface FacebookInitialSyncResponse {
  message: string;
  connectedPages: Array<{ pageId: string; pageName: string }>;
}

interface FacebookSendMessageResponse {
  messageId: string;
  recipientId: string;
}

export const FACEBOOK_OUTSIDE_ALLOWED_WINDOW_ERROR_CODE =
  'FACEBOOK_OUTSIDE_ALLOWED_WINDOW';

export const getFacebookIntegrationUrl = async (
  teamId: string,
): Promise<{ message: string }> => {
  try {
    const { data } = await getFacebookApi().get(FACEBOOK_OAUTH_URL_PATH, {
      params: { teamId },
    });
    return data;
  } catch (error) {
    log.error('Error fetching Facebook OAuth URL:', error);
    throw error;
  }
};

export const scheduleFacebookInitialSync = async (params: {
  teamId: string;
  code: string;
  state: string;
}): Promise<FacebookInitialSyncResponse> => {
  try {
    const { data } = await getFacebookApi().post(
      FACEBOOK_INITIAL_SYNC_PATH,
      params,
    );
    return data;
  } catch (error) {
    log.error('Error scheduling Facebook initial sync:', error);
    throw error;
  }
};

/**
 * Sends a plain-text message to a Facebook Messenger recipient via the backend
 * POST /send-message endpoint, which proxies to the Facebook Graph API.
 *
 * Meta 24-hour messaging window constraint: Facebook Messenger only permits
 * outbound messages within 24 hours of the recipient's last inbound message.
 * Outside that window the backend returns HTTP 400 with body
 * `{ code: 'FACEBOOK_OUTSIDE_ALLOWED_WINDOW', message }`. Callers can
 * detect this by catching the error and checking
 * `error.response?.data?.code === FACEBOOK_OUTSIDE_ALLOWED_WINDOW_ERROR_CODE`,
 * then rendering a specific UI state rather than a generic error toast.
 *
 * Only plain text is supported today; attachment support will be added later.
 */
export const sendFacebookMessage = async (params: {
  teamId: string;
  pageId: string;
  recipientId: string;
  text: string;
}): Promise<FacebookSendMessageResponse> => {
  try {
    const { data } = await getFacebookApi().post(
      FACEBOOK_SEND_MESSAGE_PATH,
      params,
    );
    return data;
  } catch (error) {
    log.error('Error sending Facebook message:', error);
    throw error;
  }
};

export const disconnectFacebookIntegration = async (params: {
  teamId: string;
  pageId: string;
  deleteData: boolean;
}): Promise<void> => {
  try {
    await getFacebookApi().delete(
      `${FACEBOOK_UNSUBSCRIBE_PATH}/${params.teamId}/${params.pageId}`,
      { params: { deleteData: params.deleteData } },
    );
  } catch (error) {
    log.error('Error disconnecting Facebook integration:', error);
    throw error;
  }
};
